import cn from 'classnames';
import React, { PropTypes } from 'react';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Immutable from 'immutable';
import { Tabs, TabContent } from '@bibliocommons/base-tabs';
import BcReactComponent from '@bibliocommons/base-bc-react-component';
import BibBrief from '@bibliocommons/shared-bib-brief';
import FullScreenOverlay from '@bibliocommons/base-full-screen-overlay';
import JacketCover from '@bibliocommons/shared-jacket-cover';
import { Placeholder } from '@bibliocommons/base-table';
import AvailabilityTable from '../AvailabilityTable';
import DocumentHeadContainer from '../../../containers/DocumentHeadContainer';
import HoldingsProbeContainer from '../../../containers/HoldingsProbeContainer';
import StackMapContainer from '../../../containers/StackMapContainer';
import FormatIndicator from '@bibliocommons/format-indicator';
import MarcHoldingsTable from '../MarcHoldingsTable';
import SubscriptionTable from '../SubscriptionTable';
import EResourceBlock from '../EResourceBlock';
import './AvailabilityDetailOverlay.scss';
import { AlertFailure } from '@bibliocommons/base-alerts';
import { bibJacketCoverAltText } from '@bibliocommons/utils-item';

const propTypes = {
  bib: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
  intl: intlShape,
  availabilityDetail: PropTypes.shape({
    marcHoldings: PropTypes.object,
    subscriptions: PropTypes.object,
    items: PropTypes.object,
    format: PropTypes.string,
    jacket: PropTypes.object,
    title: PropTypes.string,
    subtitle: PropTypes.string
  }),
  stackMap: ImmutablePropTypes.mapContains({
    enabled: PropTypes.bool,
    branches: ImmutablePropTypes.listOf(PropTypes.string),
    domain: PropTypes.string
  }),
  openStackMap: PropTypes.func.isRequired,
  disableClose: PropTypes.bool,
  standalone: PropTypes.bool,
  showDueDateInCirc: PropTypes.bool,
  showCollectionInCirc: PropTypes.bool,
  isILSDown: PropTypes.bool
};

export class AvailabilityDetailOverlay extends BcReactComponent {
  constructor(props) {
    super(props);

    this.bindAll('renderAvailabilityTables',
               'renderSubscriptionTables',
               'renderMarcHoldingsTables');
  }
  renderSubscriptionTables() {
    const { intl } = this.props;
    const { subscriptions } = this.props.availabilityDetail;
    if (subscriptions && subscriptions.size < 1) {
      return null;
    }

    return (
      <TabContent eventKey={1} label={intl.formatMessage({ id: 'availability_modal_tab_subscription' })}>
        <SubscriptionTable subscriptions={subscriptions} />
      </TabContent>
    );
  }

  renderMarcHoldingsTables() {
    const { intl } = this.props;
    const { marcHoldings } = this.props.availabilityDetail;
    if (marcHoldings && marcHoldings.size < 1) {
      return null;
    }
    return (
      <TabContent eventKey={2} label={intl.formatMessage({ id: 'availability_modal_tab_marc_holdings' })}>
        <h3>Marc Holdings</h3>
        <MarcHoldingsTable rows={marcHoldings} />
      </TabContent>
    );
  }

  renderAvailabilityTables() {
    const { intl, stackMap, openStackMap, showDueDateInCirc, showCollectionInCirc } = this.props;
    const itemGroup = this.props.availabilityDetail.items;
    if (!itemGroup || itemGroup.size < 1) {
      return null;
    }

    const tables = itemGroup.map(availability => <AvailabilityTable
      stackMap={stackMap}
      openStackMap={openStackMap}
      key={availability.get('key')}
      status={availability.get('status')}
      rows={availability.get('items')}
      showDueDateInCirc={showDueDateInCirc}
      showCollectionInCirc={showCollectionInCirc} />);

    return (
      <TabContent eventKey={0} label={intl.formatMessage({ id: 'availability_modal_tab_circulation' })}>
        {tables}
      </TabContent>
    );
  }

  renderEResources() {
    const url = this.props.bib.getIn(['availability', 'eresourceUrl']);
    const description = this.props.bib.getIn(['availability', 'eresourceDescription']);
    if (url) {
      // TODO: This should be updated to use pass in all eResources instead
      const eResources = Immutable.List.of(Immutable.Map({ description, url }));
      return <EResourceBlock eResources={eResources} format={this.props.bib.getIn(['briefInfo', 'format'])} />;
    }

    return null;
  }

  renderOverlayContent() {
    if (this.props.open) {
      const { bib, isILSDown } = this.props;
      const bibCoverAltText = bibJacketCoverAltText(bib.getIn(['briefInfo', 'title']), bib.getIn(['briefInfo', 'authors']));

      return (
        <div>
          <FormattedMessage tagName="h2" id="availabilityFor" />
          <div className="availability-title-block">
            <div className="availability-title-block-jacket">
              <JacketCover
                src={bib.getIn(['briefInfo', 'jacket'])}
                format={bib.getIn(['briefInfo', 'format'])}
                alt={bibCoverAltText} />
            </div>
            <div className="availability-title-block-brief">
              <BibBrief
                bib={bib.get('briefInfo')}
                titleTag="h1"
                renderAuthorsAsLink={!this.context.mobileApp} />
              <FormatIndicator format={bib.getIn(['briefInfo', 'format'])} />
              <span> &ndash; { bib.getIn(['briefInfo', 'publicationDate']) }</span>
              <br />
              <span>{this.props.intl.formatMessage({ id: 'totalCopies' }, { totalCopies: bib.getIn(['availability', 'totalCopies']) })}</span>
            </div>
            <HoldingsProbeContainer id={this.props.bib.get('id')} />
          </div>
          <hr />
          { isILSDown && <AlertFailure><FormattedMessage id="errors_ils_item_availability_error" /></AlertFailure> }
          {this.renderEResources()}
          {this.renderTabs()}
        </div>
      );
    }

    return null;
  }

  renderTabs() {
    if (this.props.isFetching) {
      return <Placeholder />;
    }

    return (
      <Tabs defaultActiveKey={0}>
        { this.renderAvailabilityTables() }
        { this.renderSubscriptionTables() }
      </Tabs>
    );
  }

  render() {
    const { intl } = this.props;
    const className = cn('cp-availability-details-overlay', { 'is-standalone': this.props.standalone });
    return (
      <FullScreenOverlay open={this.props.open} handleClose={this.props.handleClose} disableHistoryControls disableClose={this.props.disableClose} className={className}>
        <DocumentHeadContainer pageTitle={intl.formatMessage({ id: 'availabilityForTitle' }, { title: this.props.bib.getIn(['briefInfo', 'title']) })} />
        {this.renderOverlayContent()}
        <StackMapContainer />
      </FullScreenOverlay>
    );
  }
}

AvailabilityDetailOverlay.propTypes = propTypes;

AvailabilityDetailOverlay.contextTypes = {
  mobileApp: PropTypes.bool
};

export default injectIntl(AvailabilityDetailOverlay);



// WEBPACK FOOTER //
// ./app/components/shared/AvailabilityDetailOverlay/AvailabilityDetailOverlay.jsx